import React           from 'react';
import { observer }    from 'mobx-react-lite';
import debounce        from 'lodash/debounce';
import View            from '@widesk-ui/components/View';
import TextArea        from '@widesk-ui/components/form/TextArea';
import Segmented       from '@widesk-ui/Segmented';
import useDesignTokens from '@/hooks/useDesignTokens';

const SUBMIT_DELAY_MS = 600;

type TextContentFieldProps = {
	value?: string;
	mixed?: boolean;
	/** Nom du point d'intérêt de la forme — absent pour une forme sans point d'intérêt. */
	linkedLabel?: string;
	bound?: boolean;
	onSubmit: (value: string) => void;
	onBind?: () => void;
	onUnbind?: () => void;
};

const TextContentField = observer((props: TextContentFieldProps) => {
	const tokens = useDesignTokens();
	const [draft, setDraft] = React.useState(props.value ?? '');
	const [justUnbound, setJustUnbound] = React.useState(false);

	const onSubmitRef = React.useRef(props.onSubmit);
	onSubmitRef.current = props.onSubmit;

	const submit = React.useMemo(() => debounce((value: string) => onSubmitRef.current(value), SUBMIT_DELAY_MS), []);

	React.useEffect(() => () => submit.flush(), [submit]);

	React.useEffect(() => setDraft(props.value ?? ''), [props.value]);

	const linked = props.linkedLabel !== undefined;

	return (
		<View gap="sm">
			{linked && (
				<Segmented
					block
					value={props.bound ? 'place' : 'free'}
					onChange={value => {
						if (value === 'place') {
							submit.cancel();
							props.onBind?.();
						} else {
							setJustUnbound(true);
							props.onUnbind?.();
						}
					}}
					options={[
						{ value: 'place', label: 'Nom du point d\'intérêt' },
						{ value: 'free', label: 'Texte libre' },
					]}
				/>
			)}

			{!props.bound && (
				<View gap={6}>
					<TextArea
						autoFocus={justUnbound}
						value={draft}
						placeholder={props.mixed && !draft ? 'Valeurs différentes' : 'Votre texte'}
						autoSize={{ minRows: 1, maxRows: 5 }}
						onChange={e => {
							setDraft(e.target.value);
							submit(e.target.value);
						}}
						style={{ paddingTop: 9, paddingBottom: 9 }}
					/>

					<View size={12} color={tokens.text.muted}>Entrée pour passer à la ligne.</View>
				</View>
			)}
		</View>
	);
});

export default TextContentField;
